import React, { useState } from 'react';
import Modal from './Modal';
import { projectsApi } from '../api/client';

interface AddProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectAdded: () => void;
}

const AddProjectModal: React.FC<AddProjectModalProps> = ({ isOpen, onClose, onProjectAdded }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 'active' as 'active' | 'past',
    technologies: '',
    status: 'planning',
    category: 'personal',
    priority: 'medium' as 'high' | 'medium' | 'low',
    startDate: '',
    targetDate: '',
    progress: 0,
    repository: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      type: 'active',
      technologies: '',
      status: 'planning',
      category: 'personal',
      priority: 'medium',
      startDate: '',
      targetDate: '',
      progress: 0,
      repository: ''
    });
    setError(null);
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Project name is required');
      return;
    }

    if (!formData.description.trim()) {
      setError('Project description is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await projectsApi.addProject({
        name: formData.name,
        description: formData.description,
        type: formData.type,
        technologies: formData.technologies ? formData.technologies.split(',').map(t => t.trim()) : [],
        status: formData.status,
        category: formData.category,
        priority: formData.priority,
        startDate: formData.startDate || undefined,
        targetDate: formData.targetDate || undefined,
        progress: formData.progress,
        repository: formData.repository || undefined
      });

      setSuccess(true);
      setTimeout(() => {
        onProjectAdded();
        handleClose();
      }, 1500);
    } catch (err) {
      console.error('Error adding project:', err);
      setError('Failed to add project. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Create New Project">
      <form onSubmit={handleSubmit} className="modal-form">
        {error && (
          <div className="modal-error">
            {error}
          </div>
        )}

        {success && (
          <div className="modal-success">
            Project created successfully!
          </div>
        )}

        <div className="modal-form-group">
          <label>Project Name *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Enter project name..."
            disabled={loading}
          />
        </div>

        <div className="modal-form-group">
          <label>Description *</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            placeholder="Describe your project..."
            disabled={loading}
            rows={3}
          />
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Type</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value as 'active' | 'past' })}
              disabled={loading}
            >
              <option value="active">Active Project</option>
              <option value="past">Past Project</option>
            </select>
          </div>

          <div className="modal-form-group">
            <label>Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              disabled={loading}
            >
              <option value="personal">Personal</option>
              <option value="work">Work</option>
              <option value="learning">Learning</option>
              <option value="open-source">Open Source</option>
              <option value="client">Client</option>
            </select>
          </div>
        </div>

        <div className="modal-form-row">
          <div className="modal-form-group">
            <label>Priority</label>
            <select
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: e.target.value as 'high' | 'medium' | 'low' })}
              disabled={loading}
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          <div className="modal-form-group">
            <label>Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              disabled={loading}
            >
              <option value="planning">Planning</option>
              <option value="in-progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="on-hold">On Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        <div className="modal-form-group">
          <label>Technologies</label>
          <input
            type="text"
            value={formData.technologies}
            onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
            placeholder="React, Node.js, TypeScript (comma separated)"
            disabled={loading}
          />
        </div>

        {formData.type === 'active' && (
          <>
            <div className="modal-form-row">
              <div className="modal-form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  disabled={loading}
                />
              </div>

              <div className="modal-form-group">
                <label>Target Date</label>
                <input
                  type="date"
                  value={formData.targetDate}
                  onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="modal-form-row">
              <div className="modal-form-group">
                <label>Progress (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={formData.progress}
                  onChange={(e) => setFormData({ ...formData, progress: parseInt(e.target.value) || 0 })}
                  disabled={loading}
                />
              </div>

              <div className="modal-form-group">
                <label>Repository URL</label>
                <input
                  type="url"
                  value={formData.repository}
                  onChange={(e) => setFormData({ ...formData, repository: e.target.value })}
                  placeholder="https://github.com/username/repo"
                  disabled={loading}
                />
              </div>
            </div>
          </>
        )}

        <div className="modal-form-actions">
          <button
            type="button"
            onClick={handleClose}
            className="modal-btn modal-btn-cancel"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="modal-btn modal-btn-primary"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Project'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default AddProjectModal;